/**
 * Checkout New - Two-column checkout layout
 *
 * This is a separate JS file from checkout.js - completely fresh start.
 * Uses Alpine.js for reactivity.
 * All sections are always visible - no accordion behavior.
 */

(function ($) {
    'use strict';

    // Debug helper
    const DEBUG = true;
    const log = (...args) => DEBUG && console.log('[CheckoutNew]', ...args);

    const CART_TOTAL_VALUE_SELECTOR = '.cart-subtotal-value, .cart-shipping-value, .cart-total-value';
    const CART_TOTALS_LOADING_TIMEOUT = 12000;
    // Keep vertical centering and rotation on separate elements. Both Tailwind
    // utilities use `transform`; combining them makes the rotation interpolate
    // away `translateY(-50%)`, which looks like the spinner is bouncing.
    const CART_VALUE_SPINNER = '<span class="cart-value-spinner pointer-events-none absolute right-0 top-1/2 inline-block h-4 w-4 -translate-y-1/2" aria-hidden="true"><span class="block h-full w-full rounded-full border-2 border-gray-200 border-t-versa-500 motion-safe:animate-spin"></span></span>';
    let cartTotalsLoadingSequence = 0;
    let activeCartTotalsLoadingToken = 0;
    let cartTotalsLoadingTimer = null;
    let cartTotalsRefreshTimer = null;

    /** Removed-items list identity: product + Torg Pricing pack size (a pack line is its own item). */
    function sameRemovedItem(a, b) {
        return a.product_id === b.product_id && (parseInt(a.torg_pack) || 0) === (parseInt(b.torg_pack) || 0);
    }

    /**
     * Show a restrained loading state only where cart amounts are rendered.
     * Existing values stay in the layout (hidden) so the totals card does not jump.
     */
    function renderCartTotalsLoadingState() {
        if (!activeCartTotalsLoadingToken) return;

        const loadingLabel = window.checkoutNewData?.i18n?.updatingTotals || 'Updating totals...';

        $(CART_TOTAL_VALUE_SELECTOR).each(function () {
            const $value = $(this);
            $value
                .addClass('is-cart-value-loading')
                .attr({
                    'aria-busy': 'true',
                    'aria-label': loadingLabel,
                });

            $value.children().not('.cart-value-spinner').css('visibility', 'hidden');

            if (!$value.children('.cart-value-spinner').length) {
                $value.append(CART_VALUE_SPINNER);
            }
        });
    }

    function beginCartTotalsLoading() {
        const token = ++cartTotalsLoadingSequence;
        activeCartTotalsLoadingToken = token;

        if (cartTotalsLoadingTimer) {
            clearTimeout(cartTotalsLoadingTimer);
        }

        renderCartTotalsLoadingState();
        cartTotalsLoadingTimer = setTimeout(function () {
            completeCartTotalsLoading(token);
        }, CART_TOTALS_LOADING_TIMEOUT);

        return token;
    }

    /**
     * DOM total updates replace the spinner too. Reapply it until the latest
     * shipping/totals request has completed.
     */
    function syncCartTotalsLoadingState() {
        renderCartTotalsLoadingState();
    }

    function completeCartTotalsLoading(token) {
        if (!activeCartTotalsLoadingToken || token !== activeCartTotalsLoadingToken) return;

        activeCartTotalsLoadingToken = 0;
        clearTimeout(cartTotalsLoadingTimer);
        cartTotalsLoadingTimer = null;

        $(CART_TOTAL_VALUE_SELECTOR).each(function () {
            const $value = $(this);
            $value.children('.cart-value-spinner').remove();
            $value.children().css('visibility', '');
            $value
                .removeClass('is-cart-value-loading')
                .removeAttr('aria-busy aria-label');
        });
    }

    /**
     * Main checkout state and methods
     * Used with Alpine.js x-data="checkoutNew()"
     */
    window.checkoutNew = function () {
        return {
            // Customer details
            customer: {
                email: '',
                phone: '',
                personnummer: '',
                firstName: '',
                lastName: '',
                address: '',
                address2: '',
                postalCode: '',
                city: '',
                country: 'SE',
            },

            // Login modal state
            loginModalOpen: false,
            activeTab: 'login',

            // UI state
            loading: false,
            fetchingAddress: false,
            submitting: false,
            showCoField: false,
            errors: {},

            // Section state
            shippingConfirmed: false,
            shippingSelected: false, // True when any shipping method is selected
            selectedShippingMethod: '',
            shippingSummary: null, // { method, servicePoint, cost }

            // Compact cart state
            cartExpanded: false,

            // Cart empty state and removed items recovery
            cartEmpty: false,
            removedItems: [], // Items removed during session for undo/recovery

            // Cart summary for mobile collapsed view
            cartSummary: {
                itemCount: 0,
                total: ''
            },

            // Checkboxes
            subscribeNewsletter: false,

            // Submit state
            submitError: null,

            /**
             * Initialize checkout
             */
            init() {
                log('Initializing checkout...');

                // Track checkout session start
                this.trackStep('cart');

                // Load customer data from server if available
                if (window.checkoutNewData?.customer) {
                    this.customer = { ...this.customer, ...window.checkoutNewData.customer };
                }

                // Load removed items from sessionStorage
                this.loadRemovedItems();

                // Check if page loaded with empty cart (set by PHP)
                const container = document.querySelector('.checkout-new-container');
                const pageLoadedEmpty = container?.dataset.cartEmpty === 'true';

                if (pageLoadedEmpty) {
                    log('Page loaded with empty cart, checking for recovery items...');
                    this.cartEmpty = true;

                    if (this.removedItems.length > 0) {
                        log('Found', this.removedItems.length, 'removed items, injecting recovery UI');
                        // Inject removed items into server-rendered empty state
                        this.injectRemovedItemsIntoEmptyState();
                    } else {
                        log('No removed items found');
                    }
                }

                // Listen for WooCommerce events
                $(document.body).on('updated_checkout', () => {
                    log('WooCommerce checkout updated');

                    // Woo may finish update_order_review after CSA's immediate
                    // sidebar request. Refresh once more from the settled
                    // server state so a stale total can never remain visible.
                    scheduleCartTotalsRefresh();

                    // If cart is empty, WooCommerce just replaced our content - re-show empty state
                    if (this.cartEmpty) {
                        log('Cart is empty after WC update, re-showing empty state');
                        this.showEmptyCartState();
                    }
                });

                // Sync form fields on customer data change
                this.$watch('customer', () => {
                    this.syncFormFields();
                });

                // Lock background scroll when cart expanded on mobile
                this.$watch('cartExpanded', (expanded) => {
                    if (window.innerWidth < 1024) {
                        document.body.classList.toggle('overflow-hidden', expanded);
                    }
                });

                log('Checkout initialized', { customer: this.customer });
            },

            /**
             * Toggle compact cart expansion
             */
            toggleCart() {
                this.cartExpanded = !this.cartExpanded;
                log('Cart toggled:', this.cartExpanded);
            },

            // =========================================================================
            // REMOVED ITEMS & UNDO FUNCTIONALITY
            // =========================================================================

            /**
             * Load removed items from sessionStorage (with 30 min expiry)
             */
            loadRemovedItems() {
                try {
                    const stored = sessionStorage.getItem('checkout_removed_items');
                    if (stored) {
                        const data = JSON.parse(stored);
                        const thirtyMinutes = 30 * 60 * 1000;
                        // Filter out expired items
                        this.removedItems = data.filter(item =>
                            Date.now() - item.timestamp < thirtyMinutes
                        );
                        // Save back filtered list
                        if (this.removedItems.length !== data.length) {
                            this.saveRemovedItems();
                        }
                        log('Loaded removed items:', this.removedItems.length);
                    }
                } catch (e) {
                    log('Error loading removed items:', e);
                }
            },

            /**
             * Save removed items to sessionStorage
             */
            saveRemovedItems() {
                try {
                    sessionStorage.setItem('checkout_removed_items', JSON.stringify(this.removedItems));
                } catch (e) {
                    log('Error saving removed items:', e);
                }
            },

            /**
             * Add item to removed items list
             */
            addRemovedItem(itemData) {
                // Avoid duplicates (a Torg pack line and a normal line of the same product are two items)
                this.removedItems = this.removedItems.filter(
                    item => !sameRemovedItem(item, itemData)
                );
                this.removedItems.push({
                    ...itemData,
                    timestamp: Date.now()
                });
                this.saveRemovedItems();
                log('Added to removed items:', itemData.name);
            },

            /**
             * Remove item from removed items list (after restore)
             */
            removeFromRemovedItems(productId, torgPack) {
                this.removedItems = this.removedItems.filter(
                    item => !(item.product_id === productId && (parseInt(item.torg_pack) || 0) === (parseInt(torgPack) || 0))
                );
                this.saveRemovedItems();
            },

            /**
             * Show undo toast notification
             */
            showUndoToast(item) {
                const toastId = `toast-${item.product_id}-${Date.now()}`;

                // Truncate long product names
                const maxNameLength = 30;
                const displayName = item.name.length > maxNameLength
                    ? item.name.substring(0, maxNameLength) + '...'
                    : item.name;

                // Create toast element
                const toast = document.createElement('div');
                toast.id = toastId;
                toast.className = 'checkout-undo-toast fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm bg-gray-900 text-white rounded-xl shadow-2xl p-4 z-50 transform translate-y-full opacity-0 transition-all duration-300';
                toast.innerHTML = `
                    <div class="flex items-center gap-3">
                        <img src="${item.thumbnail}" alt="" class="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                        <span class="text-sm flex-1 min-w-0">${displayName} borttagen</span>
                        <button type="button" class="undo-btn px-4 py-2 bg-white text-gray-900 rounded-lg text-sm font-semibold hover:bg-gray-100 transition-colors flex-shrink-0">
                            Ångra
                        </button>
                    </div>
                `;

                document.body.appendChild(toast);

                // Animate in
                requestAnimationFrame(() => {
                    toast.classList.remove('translate-y-full', 'opacity-0');
                });

                // Store reference to this for event handlers
                const self = this;

                // Undo click handler
                toast.querySelector('.undo-btn').addEventListener('click', () => {
                    self.restoreItem(item);
                    self.dismissToast(toastId);
                });

                // Auto-dismiss after 8 seconds
                const dismissTimer = setTimeout(() => this.dismissToast(toastId), 8000);

                // Store timer reference for cleanup
                toast.dataset.dismissTimer = dismissTimer;

                log('Showing undo toast for:', item.name);
            },

            /**
             * Dismiss toast notification
             */
            dismissToast(toastId) {
                const toast = document.getElementById(toastId);
                if (toast) {
                    // Clear any pending timer
                    if (toast.dataset.dismissTimer) {
                        clearTimeout(parseInt(toast.dataset.dismissTimer));
                    }
                    // Animate out
                    toast.classList.add('translate-y-full', 'opacity-0');
                    setTimeout(() => toast.remove(), 300);
                }
            },

            /**
             * Restore a removed item to the cart
             */
            async restoreItem(item) {
                log('Restoring item:', item.name);

                // Track if we're restoring from empty state (need page reload)
                const wasEmpty = this.cartEmpty;

                try {
                    const response = await $.ajax({
                        url: window.checkoutNewData?.ajaxUrl,
                        method: 'POST',
                        data: {
                            action: 'restore_cart_item',
                            product_id: item.product_id,
                            variation_id: item.variation_id || 0,
                            quantity: item.quantity || 1,
                            // Torg Pricing pack size: restores the pack line ("… – 10-pack").
                            torg_pack: parseInt(item.torg_pack) || 0,
                            _wpnonce: window.checkoutNewData?.nonce,
                        },
                    });

                    if (response.success) {
                        log('Item restored successfully');

                        // Remove from removed items list
                        this.removeFromRemovedItems(item.product_id, item.torg_pack);

                        // If restoring from empty state, reload page to reinitialize checkout
                        if (wasEmpty) {
                            log('Was empty, reloading page to reinitialize checkout');
                            window.location.reload();
                            return;
                        }

                        // Update cart display
                        this.updateCartAfterRestore(response.data);

                        // Hide empty state if showing
                        this.cartEmpty = false;
                    } else {
                        log('Error restoring item:', response.data?.message);
                        alert(response.data?.message || 'Kunde inte återställa produkten');
                    }
                } catch (e) {
                    log('AJAX error restoring item:', e);
                    alert('Ett fel uppstod. Försök igen.');
                }
            },

            /**
             * Update cart display after restoring an item
             */
            updateCartAfterRestore(data) {
                // Add the new item HTML to cart
                if (data.item_html) {
                    // Only target cart item containers, not deals section
                    const cartContainers = document.querySelectorAll('.cart-section .divide-y.divide-gray-100, .compact-cart-section .divide-y.divide-gray-100');

                    cartContainers.forEach(container => {
                        container.insertAdjacentHTML('beforeend', data.item_html);
                    });
                }

                // Update totals
                this.updateCartTotals(data.cart_total, data.item_count);

                // Trigger shipping recalculation
                window.dispatchEvent(new CustomEvent('csa:cart_changed'));
            },

            /**
             * Show empty cart state - creates the UI dynamically
             * Called when WooCommerce replaces our content
             */
            showEmptyCartState() {
                log('Showing empty cart state');

                // Find or create the container
                const checkoutContainer = document.querySelector('.checkout-new-container');
                if (!checkoutContainer) {
                    log('No checkout container found');
                    return;
                }

                // Hide the form if it exists
                const form = checkoutContainer.querySelector('form.checkout');
                if (form) {
                    form.style.display = 'none';
                }

                // Hide the sidebar
                const sidebar = checkoutContainer.querySelector('.lg\\:block.w-\\[380px\\]');
                if (sidebar) {
                    sidebar.style.display = 'none';
                }

                // Check if empty cart state already exists
                let emptyState = checkoutContainer.querySelector('.empty-cart-injected');
                if (!emptyState) {
                    // Create the empty cart state HTML
                    emptyState = document.createElement('div');
                    emptyState.className = 'empty-cart-injected max-w-2xl mx-auto px-4 py-12';

                    // Insert after the form or at the end of container
                    const leftColumn = checkoutContainer.querySelector('.flex-1.bg-white');
                    if (leftColumn) {
                        leftColumn.appendChild(emptyState);
                    } else {
                        checkoutContainer.appendChild(emptyState);
                    }
                }

                // Build the removed items HTML
                let removedItemsHtml = '';
                if (this.removedItems.length > 0) {
                    const itemsHtml = this.removedItems.map(item => {
                        // Truncate long names
                        const maxLen = 45;
                        const displayName = item.name.length > maxLen
                            ? item.name.substring(0, maxLen) + '...'
                            : item.name;

                        return `
                        <div class="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
                            <div class="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-lg overflow-hidden">
                                <img src="${item.thumbnail}" alt="" class="w-full h-full object-cover" />
                            </div>
                            <div class="flex-1 min-w-0 overflow-hidden">
                                <p class="text-sm font-medium text-gray-900 truncate" title="${item.name}">${displayName}</p>
                                <p class="text-xs text-gray-500">Antal: ${item.quantity} · ${item.price}</p>
                            </div>
                            <button type="button" onclick="window.restoreCartItem(${item.product_id}, ${item.quantity}, ${item.variation_id || 0}, ${parseInt(item.torg_pack) || 0})"
                                    class="flex-shrink-0 px-3 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap">
                                Lägg till
                            </button>
                        </div>
                    `;}).join('');

                    removedItemsHtml = `
                        <div class="mt-8 bg-gray-50 rounded-xl p-5">
                            <h4 class="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2">
                                <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Nyligen borttagna
                            </h4>
                            <div class="space-y-3">${itemsHtml}</div>
                        </div>
                    `;
                }

                // Update the empty state content
                emptyState.innerHTML = `
                    <div class="text-center">
                        <div class="w-20 h-20 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                            <svg class="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
                            </svg>
                        </div>
                        <h3 class="text-xl font-semibold text-gray-900 mb-2">Din varukorg är tom</h3>
                        <p class="text-gray-500 mb-6">Det finns inga produkter i din varukorg just nu.</p>
                    </div>
                    ${removedItemsHtml}
                    <div class="mt-8 text-center">
                        <a href="${window.checkoutNewData?.shopUrl || '/'}" class="inline-flex items-center gap-2 text-versa-600 hover:text-versa-700 font-medium transition-colors">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                            </svg>
                            Fortsätt handla
                        </a>
                    </div>
                `;

                // Make sure it's visible
                emptyState.style.display = 'block';
            },

            /**
             * Inject removed items into server-rendered empty state
             * Used when page loads with empty cart and there are items in sessionStorage
             */
            injectRemovedItemsIntoEmptyState() {
                const placeholder = document.getElementById('empty-cart-recovery-placeholder');
                if (!placeholder) {
                    log('No recovery placeholder found');
                    return;
                }

                if (this.removedItems.length === 0) {
                    return;
                }

                const itemsHtml = this.removedItems.map(item => {
                    const maxLen = 45;
                    const displayName = item.name.length > maxLen
                        ? item.name.substring(0, maxLen) + '...'
                        : item.name;

                    return `
                    <div class="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
                        <div class="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-lg overflow-hidden">
                            <img src="${item.thumbnail}" alt="" class="w-full h-full object-cover" />
                        </div>
                        <div class="flex-1 min-w-0 overflow-hidden">
                            <p class="text-sm font-medium text-gray-900 truncate" title="${item.name}">${displayName}</p>
                            <p class="text-xs text-gray-500">Antal: ${item.quantity} · ${item.price}</p>
                        </div>
                        <button type="button" onclick="window.restoreCartItem(${item.product_id}, ${item.quantity}, ${item.variation_id || 0}, ${parseInt(item.torg_pack) || 0})"
                                class="flex-shrink-0 px-3 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap">
                            Lägg till
                        </button>
                    </div>
                `;
                }).join('');

                placeholder.innerHTML = `
                    <div class="mt-8 bg-gray-50 rounded-xl p-5">
                        <h4 class="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2">
                            <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Nyligen borttagna
                        </h4>
                        <div class="space-y-3">${itemsHtml}</div>
                    </div>
                `;

                log('Injected', this.removedItems.length, 'removed items into empty state');
            },

            /**
             * Update cart totals display
             */
            updateCartTotals(total, itemCount) {
                // Update total displays
                document.querySelectorAll('.cart-total-value').forEach(el => {
                    el.innerHTML = total;
                });

                // Update item count displays
                document.querySelectorAll('.cart-summary-count').forEach(el => {
                    const plural = itemCount === 1 ? 'produkt' : 'produkter';
                    el.textContent = `${itemCount} ${plural}`;
                });

                document.querySelectorAll('.cart-summary-total').forEach(el => {
                    el.innerHTML = total.replace(/<[^>]*>/g, ''); // Strip HTML
                });

                // Update compact cart summary
                this.cartSummary.itemCount = itemCount;
                this.cartSummary.total = total;

                // Rebuild stacked thumbnails from cart items in DOM
                const stack = document.querySelector('.cart-thumbnails-stack');
                if (stack) {
                    const cartItems = document.querySelectorAll('.compact-cart-section .cart-item[data-thumbnail], .cart-section .cart-item[data-thumbnail]');
                    const seen = new Set();
                    const thumbs = [];
                    cartItems.forEach(item => {
                        const url = item.dataset.thumbnail;
                        const name = item.dataset.productName || '';
                        if (url && !seen.has(url)) {
                            seen.add(url);
                            thumbs.push({ url, name });
                        }
                    });

                    let html = '';
                    thumbs.slice(0, 3).forEach((t, i) => {
                        html += `<div class="relative w-10 h-10 rounded-full overflow-hidden ring-2 ring-white shadow-sm" style="z-index: ${10 - i};">
                            <img src="${t.url}" alt="${t.name}" class="w-full h-full object-cover" />
                        </div>`;
                    });
                    if (thumbs.length > 3) {
                        html += `<div class="relative w-10 h-10 rounded-full bg-gray-200 ring-2 ring-white shadow-sm flex items-center justify-center" style="z-index: 6;">
                            <span class="text-xs font-semibold text-gray-600">+${thumbs.length - 3}</span>
                        </div>`;
                    }
                    stack.innerHTML = html;
                }
            },

            /**
             * Simple email validation
             */
            isValidEmail(email) {
                return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
            },

            /**
             * Fetch address from personnummer (placeholder)
             */
            async fetchAddressFromPersonnummer() {
                if (!this.customer.personnummer) {
                    this.errors.personnummer = 'Please enter a personal number';
                    return;
                }

                this.fetchingAddress = true;
                this.errors.personnummer = null;

                try {
                    const response = await $.ajax({
                        url: window.checkoutNewData?.ajaxUrl,
                        method: 'POST',
                        data: {
                            action: 'checkout_new_fetch_address',
                            nonce: window.checkoutNewData?.nonce,
                            personnummer: this.customer.personnummer,
                        },
                    });

                    if (response.success) {
                        if (response.data.placeholder) {
                            // Placeholder response - show message
                            this.errors.personnummer = response.data.message;
                        } else {
                            // Real API response - populate fields
                            this.customer.firstName = response.data.firstName || this.customer.firstName;
                            this.customer.lastName = response.data.lastName || this.customer.lastName;
                            this.customer.address = response.data.address || this.customer.address;
                            this.customer.postalCode = response.data.postalCode || this.customer.postalCode;
                            this.customer.city = response.data.city || this.customer.city;
                            log('Address fetched:', response.data);
                        }
                    } else {
                        this.errors.personnummer = response.data?.message || 'Failed to fetch address';
                    }
                } catch (error) {
                    log('Error fetching address:', error);
                    this.errors.personnummer = 'Failed to fetch address. Please try again.';
                } finally {
                    this.fetchingAddress = false;
                }
            },

            /**
             * Update WooCommerce order review
             */
            async updateOrderReview() {
                log('Updating order review...');

                // Update hidden form fields
                this.syncFormFields();

                // Trigger WooCommerce update
                $(document.body).trigger('update_checkout');
            },

            /**
             * Sync Alpine state to hidden WooCommerce form fields
             */
            syncFormFields() {
                // Map customer data to WooCommerce field names
                const fieldMap = {
                    'billing_email': this.customer.email,
                    'billing_phone': this.customer.phone,
                    'billing_first_name': this.customer.firstName,
                    'billing_last_name': this.customer.lastName,
                    'billing_address_1': this.customer.address,
                    'billing_address_2': this.customer.address2,
                    'billing_postcode': this.customer.postalCode,
                    'billing_city': this.customer.city,
                    'billing_country': this.customer.country,
                    // Also set shipping fields
                    'shipping_first_name': this.customer.firstName,
                    'shipping_last_name': this.customer.lastName,
                    'shipping_address_1': this.customer.address,
                    'shipping_address_2': this.customer.address2,
                    'shipping_postcode': this.customer.postalCode,
                    'shipping_city': this.customer.city,
                    'shipping_country': this.customer.country,
                };

                for (const [fieldName, value] of Object.entries(fieldMap)) {
                    const $field = $(`#${fieldName}, [name="${fieldName}"]`);
                    if ($field.length && value) {
                        $field.val(value);
                    }
                }

                log('Form fields synced');
            },

            /**
             * Format price for display
             */
            formatPrice(amount) {
                const symbol = window.checkoutNewData?.currencySymbol || 'kr';
                return `${parseFloat(amount).toFixed(0)} ${symbol}`;
            },

            /**
             * Track checkout step for wide logging
             * @param {string} step - Step name (cart, shipping, payment)
             */
            trackStep(step) {
                if (!window.checkoutNewData?.ajaxUrl) return;

                fetch(window.checkoutNewData.ajaxUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: new URLSearchParams({
                        action: 'checkout_log_step',
                        step: step
                    })
                }).catch(() => {
                    // Silently fail - logging shouldn't break checkout
                });

                log('Tracked step:', step);
            },

            /**
             * Track Kustom Checkout event for wide logging
             * @param {string} event - Event name
             * @param {object} data - Event data
             */
            trackKlarnaEvent(event, data = {}) {
                if (!window.checkoutNewData?.ajaxUrl) return;

                fetch(window.checkoutNewData.ajaxUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: new URLSearchParams({
                        action: 'checkout_log_klarna_event',
                        event: event,
                        data: JSON.stringify(data)
                    })
                }).catch(() => {
                    // Silently fail - logging shouldn't break checkout
                });

                log('Tracked Kustom event:', event, data);
            },

            /**
             * Confirm shipping selection and load payment
             */
            confirmShipping() {
                log('Confirming shipping...');

                // Track payment step
                this.trackStep('payment');

                // Build shipping summary
                this.shippingSummary = this.getShippingSummary();
                this.shippingConfirmed = true;

                // Tell gateway plugin to load KCO
                log('Dispatching versa-kustom:load, VersaKustom available:', typeof window.VersaKustom);
                window.dispatchEvent(new CustomEvent('versa-kustom:load'));

                log('Shipping confirmed:', this.shippingSummary);
            },

            /**
             * Scroll to a section smoothly
             */
            scrollToSection(sectionName) {
                setTimeout(() => {
                    const section = document.querySelector(`[data-section="${sectionName}"]`);
                    if (section) {
                        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                }, 100);
            },

            /**
             * Get shipping summary from selected options
             */
            getShippingSummary() {
                const summary = {
                    method: '',
                    servicePoint: null,
                    cost: ''
                };

                // Try Custom Shipping Aggregator first (uses data attributes)
                const csaSelected = document.querySelector('.shipping-method.selected[data-csa-method-title]');
                if (csaSelected) {
                    summary.method = csaSelected.dataset.csaMethodTitle || '';
                    summary.cost = csaSelected.dataset.csaPrice || '';

                    // Get service point from location button
                    const locationButton = csaSelected.querySelector('[data-csa-location-name]');
                    if (locationButton && locationButton.dataset.csaLocationName) {
                        summary.servicePoint = locationButton.dataset.csaLocationName;
                    }

                    log('Shipping summary (CSA):', summary);
                    return summary;
                }

                // Fallback: Try standard WooCommerce shipping methods
                const selectedRadio = document.querySelector(
                    'input[name="shipping_method[0]"]:checked, ' +
                    'input[name^="shipping_method"]:checked, ' +
                    '.shipping-methods input[type="radio"]:checked'
                );

                if (!selectedRadio) {
                    log('No shipping method selected');
                    return summary;
                }

                const optionContainer = selectedRadio.closest('li, label, [data-shipping-option]');

                if (optionContainer) {
                    // Try to get method name
                    const methodEl = optionContainer.querySelector('h2, h3, .shipping-method-name, .method-name');
                    if (methodEl) {
                        summary.method = methodEl.textContent.trim();
                    } else {
                        const labelEl = optionContainer.querySelector('label');
                        if (labelEl) {
                            const clone = labelEl.cloneNode(true);
                            clone.querySelectorAll('.amount, .price').forEach(el => el.remove());
                            summary.method = clone.textContent.trim().split('\n')[0].trim() || 'Shipping selected';
                        }
                    }

                    // Try to get cost
                    const costEl = optionContainer.querySelector('.woocommerce-Price-amount, .amount, .price');
                    if (costEl) {
                        summary.cost = costEl.textContent.trim();
                    }
                }

                log('Shipping summary (WC):', summary);
                return summary;
            },
        };
    };

    /**
     * Global function to restore a cart item (used by dynamically generated onclick handlers)
     * This bridges the gap between inline onclick and Alpine's restoreItem method
     */
    window.restoreCartItem = function (productId, quantity, variationId, torgPack) {
        torgPack = parseInt(torgPack) || 0;
        log('Global restoreCartItem called:', { productId, quantity, variationId, torgPack });

        // Find the Alpine component instance
        const checkoutEl = document.querySelector('[x-data*="checkoutNew"]');
        if (checkoutEl && checkoutEl.__x) {
            const alpine = checkoutEl.__x.$data;

            // Find the item in removedItems by product ID
            const item = alpine.removedItems.find(i => i.product_id === productId && (parseInt(i.torg_pack) || 0) === torgPack);
            if (item) {
                alpine.restoreItem(item);
            } else {
                // Fallback: create minimal item object
                alpine.restoreItem({
                    product_id: productId,
                    variation_id: variationId || 0,
                    quantity: quantity || 1,
                    torg_pack: torgPack,
                    name: 'Product',
                });
            }
        } else {
            log('Could not find Alpine instance');
            // Fallback: direct AJAX call
            $.ajax({
                url: window.checkoutNewData?.ajaxUrl,
                method: 'POST',
                data: {
                    action: 'restore_cart_item',
                    product_id: productId,
                    variation_id: variationId || 0,
                    quantity: quantity || 1,
                    torg_pack: torgPack,
                    _wpnonce: window.checkoutNewData?.nonce,
                },
                success: function (response) {
                    if (response.success) {
                        window.location.reload();
                    } else {
                        alert(response.data?.message || 'Kunde inte återställa produkten');
                    }
                },
                error: function () {
                    alert('Ett fel uppstod. Försök igen.');
                },
            });
        }
    };

    /**
     * Initialize when DOM is ready
     */
    $(document).ready(function () {
        log('Document ready');

        // Initialize any non-Alpine functionality here
        initQuantityHandlers();
        initCouponHandler();
        initShippingChangeListener();

        // Listen for gateway plugin events to track KCO loading
        window.addEventListener('versa-kustom:loaded', function () {
            log('KCO loaded (from gateway plugin)');
            const checkoutEl = document.querySelector('[x-data*="checkoutNew"]');
            if (checkoutEl && checkoutEl.__x) {
                checkoutEl.__x.$data.trackKlarnaEvent('kco_loaded', {});
            }
        });
    });

    /**
     * Listen for shipping method changes from CSA (Custom Shipping Aggregator)
     *
     * CSA triggers jQuery's `update_checkout` event which KCO natively handles
     * to update the existing Klarna order. We DON'T reload KCO from scratch here
     * because that would create a new order without the shipping data.
     *
     * We only need to reload KCO from scratch when:
     * 1. User first enters payment step (confirmShipping)
     * 2. Quantity or coupon changes (cart data changes)
     *
     * Shipping changes are handled by KCO's native update mechanism.
     */
    function initShippingChangeListener() {
        // CSA dispatches 'csa:selection' event when a shipping method is selected
        window.addEventListener('csa:selection', function (e) {
            log('CSA shipping selection changed:', e.detail);

            // Update the hidden field with new shipping data
            const kcoShippingField = document.getElementById('kco_shipping_data');
            if (kcoShippingField && e.detail) {
                kcoShippingField.value = JSON.stringify(e.detail);
                log('Updated kco_shipping_data hidden field');
            }

            // Update Alpine state to reflect shipping selection
            const checkoutEl = document.querySelector('[x-data*="checkoutNew"]');
            if (checkoutEl && checkoutEl.__x) {
                checkoutEl.__x.$data.shippingSelected = true;
                log('Set shippingSelected = true');
            }

            // Load KCO immediately on shipping selection
            log('Loading KCO on shipping selection');
            window.dispatchEvent(new CustomEvent('versa-kustom:load'));

            // Fetch updated cart totals to reflect new shipping cost
            refreshCartTotals();
        });

        log('Shipping change listener initialized');
    }

    /**
     * Auto-collapse the compact cart after an action (quantity update, coupon, remove)
     * Only applies in expanded flow mode when cart is open
     */
    function autoCollapseCart() {
        const el = document.querySelector('.checkout-new-container');
        if (!el || !el._x_dataStack) return;
        const data = el._x_dataStack[0];
        if (data.cartExpanded) {
            setTimeout(() => { data.cartExpanded = false; }, 700);
        }
    }

    /**
     * Refresh cart totals from server (used when shipping method changes)
     * Note: This does NOT trigger csa:cart_changed to avoid infinite loops
     */
    function refreshCartTotals() {
        log('Refreshing cart totals...');
        const loadingToken = beginCartTotalsLoading();

        $.ajax({
            url: window.checkoutNewData?.ajaxUrl,
            method: 'POST',
            data: {
                action: 'checkout_new_get_cart_totals',
                _wpnonce: window.checkoutNewData?.nonce,
            },
            success: function (response) {
                // A later shipping/cart action supersedes this request even if
                // the older HTTP response happens to arrive last.
                if (loadingToken !== activeCartTotalsLoadingToken) {
                    log('Ignoring superseded cart totals response');
                    return;
                }

                if (response.success) {
                    log('Cart totals refreshed:', response.data);

                    const selectedShippingMethod = getSelectedCsaShippingMethod();
                    const responseShippingMethod = response.data?.shipping_method || '';

                    if (
                        selectedShippingMethod
                        && responseShippingMethod !== selectedShippingMethod
                    ) {
                        log('Ignoring cart totals for stale shipping method:', {
                            selectedShippingMethod,
                            responseShippingMethod,
                        });
                        return;
                    }

                    // Update DOM directly without triggering csa:cart_changed
                    updateCartTotalsUI(response.data);
                } else {
                    log('Failed to refresh cart totals:', response);
                }
            },
            error: function (xhr, status, error) {
                log('Error refreshing cart totals:', { status, error });
            },
            complete: function () {
                completeCartTotalsLoading(loadingToken);
            },
        });
    }

    /**
     * Debounce the final totals read after Woo/CSA state has settled.
     */
    function scheduleCartTotalsRefresh() {
        if (cartTotalsRefreshTimer) {
            clearTimeout(cartTotalsRefreshTimer);
        }

        cartTotalsRefreshTimer = setTimeout(function () {
            cartTotalsRefreshTimer = null;
            refreshCartTotals();
        }, 100);
    }

    /**
     * The CSA hidden field is the browser's authoritative current selection.
     */
    function getSelectedCsaShippingMethod() {
        const field = document.getElementById('kco_shipping_data');
        if (!field?.value) return '';

        try {
            const selection = JSON.parse(field.value);
            return typeof selection?.id === 'string' ? selection.id : '';
        } catch (error) {
            log('Could not parse kco_shipping_data while validating totals:', error);
            return '';
        }
    }

    /**
     * Update cart totals UI only (no events triggered)
     * Used when shipping method changes - we don't want to trigger CSA refetch
     */
    function updateCartTotalsUI(data) {
        log('Updating cart totals UI:', data);

        if (data.cart_total) {
            $('.cart-total-value, .order-total .woocommerce-Price-amount').html(data.cart_total);
        }
        if (data.cart_subtotal) {
            $('.cart-subtotal-value').html(data.cart_subtotal);
        }
        if (data.item_count !== undefined) {
            $('.cart-section .bg-versa-100').text(data.item_count + ' ' + (data.item_count === 1 ? 'produkt' : 'produkter'));
        }
        if (data.shipping_total) {
            $('.cart-shipping-value').html(data.shipping_total);
        }
        if (data.cart_summary) {
            $('.cart-summary-items').text(data.cart_summary);
        }
        if (data.cart_total) {
            $('.cart-summary-total').html(data.cart_total + ' <span class="font-normal text-gray-500">' + (window.checkoutNewData?.i18n?.inclVat || '(incl. VAT)') + '</span>');
        }

        syncCartTotalsLoadingState();
    }

    /**
     * Apply the quantity and stepper max the server reports for a cart line
     * (update_cart_item_quantity: line_quantity, line_max_qty). Torg Pricing can
     * hold a pack line below the requested quantity: a pack line whose tier was
     * removed keeps its price only up to the quantity it had then, and every
     * decrease lowers that limit, so + must switch off at the new max.
     */
    function syncCartLineStepper(key, data) {
        const qty = parseInt(data && data.line_quantity, 10) || 0;
        const max = parseInt(data && data.line_max_qty, 10) || 0;
        if (qty <= 0 && max <= 0) {
            return;
        }
        $('.cart-item').filter(function () { return this.dataset.cartItemKey === key; }).each(function () {
            const $line = $(this);
            if (qty > 0) {
                $line.attr('data-quantity', qty).data('quantity', qty);
            }
            if (max > 0) {
                $line.attr('data-max-qty', max);
            }
            // The stepper's Alpine state drives the input (:max, x-model) and the
            // buttons (:disabled). Alpine 2 keeps it on el.__x; Alpine 3 has Alpine.$data().
            const stepper = this.querySelector('[data-qty-stepper]');
            const state = !stepper ? null : stepper.__x ? stepper.__x.$data
                : (window.Alpine && typeof window.Alpine.$data === 'function' ? window.Alpine.$data(stepper) : null);
            if (!state) {
                return;
            }
            if (max > 0) {
                state.max = max;
            }
            if (qty > 0) {
                state.qty = qty;
            }
        });
    }

    /**
     * Handle quantity changes in cart
     */
    function initQuantityHandlers() {
        // Debounce to prevent multiple rapid updates
        let updateTimeout = null;

        // Listen for custom event from Alpine
        document.addEventListener('update-cart-qty', function (e) {
            const { key } = e.detail;
            let qty = parseInt(e.detail.qty, 10) || 0;

            // Torg Pricing pack lines change by whole packs (the server rounds too), never above
            // the stepper max (stock, or Torg Pricing's limit for a pack line whose tier was removed).
            const $line = $('.cart-item').filter(function () { return this.dataset.cartItemKey === key; });
            const pack = parseInt($line.first().attr('data-torg-pack'), 10) || 0;
            if (pack > 1 && qty > 0) {
                qty = Math.ceil(qty / pack) * pack;
            }
            const maxQty = parseInt($line.first().attr('data-max-qty'), 10) || 0;
            if (maxQty > 0 && qty > maxQty) {
                qty = maxQty;
            }
            // Keep the undo/restore data in step with the line.
            if (qty > 0) {
                $line.attr('data-quantity', qty).data('quantity', qty);
            }

            log('Quantity update requested:', { key, qty });

            // Debounce - wait 300ms before sending request
            if (updateTimeout) {
                clearTimeout(updateTimeout);
            }

            updateTimeout = setTimeout(function () {
                log('Sending quantity update:', { key, qty });
                const loadingToken = beginCartTotalsLoading();

                $.ajax({
                    url: window.checkoutNewData?.ajaxUrl,
                    method: 'POST',
                    data: {
                        action: 'update_cart_item_quantity',
                        cart_item_key: key,
                        quantity: qty,
                        _wpnonce: window.checkoutNewData?.nonce,
                    },
                    success: function (response) {
                        log('Update response:', response);
                        if (response.success) {
                            // Line quantity + stepper max as the server left them
                            syncCartLineStepper(key, response.data);
                            // Update cart totals in the DOM
                            updateCartTotals(response.data);
                            // Reload KCO if it's currently loaded
                            window.dispatchEvent(new CustomEvent('versa-kustom:cart-changed'));
                            // Auto-collapse cart in compact mode
                            autoCollapseCart();
                        } else {
                            log('Error updating quantity:', response.data?.message);
                            completeCartTotalsLoading(loadingToken);
                            alert(response.data?.message || 'Failed to update quantity');
                        }
                    },
                    error: function (xhr, status, error) {
                        log('AJAX error updating quantity:', { status, error, response: xhr.responseText });
                        completeCartTotalsLoading(loadingToken);
                        alert('Failed to update cart. Please try again.');
                    },
                });
            }, 300);
        });

        // Handle remove button - with undo support
        $(document).on('click', '.ajax_remove_from_cart', function (e) {
            e.preventDefault();
            e.stopImmediatePropagation(); // Prevent WC's own handler from also firing

            const $btn = $(this);
            const $cartItem = $btn.closest('.cart-item');
            const cartItemKey = $btn.data('cart_item_key');

            // Capture item data for undo before removal
            const itemData = {
                cart_item_key: cartItemKey,
                product_id: parseInt($cartItem.data('product-id')),
                variation_id: parseInt($cartItem.data('variation-id')) || 0,
                name: $cartItem.data('product-name'),
                quantity: parseInt($cartItem.data('quantity')) || 1,
                torg_pack: parseInt($cartItem.data('torg-pack')) || 0,
                thumbnail: $cartItem.data('thumbnail'),
                price: $cartItem.data('price'),
            };

            log('Removing item:', itemData.name, cartItemKey);

            $btn.prop('disabled', true);
            const loadingToken = beginCartTotalsLoading();

            // Animate item out immediately for responsive feel
            $cartItem.css({
                'transition': 'opacity 0.3s, transform 0.3s, max-height 0.3s',
                'opacity': '0.5',
                'transform': 'translateX(-10px)',
            });

            $.ajax({
                url: window.checkoutNewData?.ajaxUrl,
                method: 'POST',
                data: {
                    action: 'remove_cart_item',
                    cart_item_key: cartItemKey,
                    _wpnonce: window.checkoutNewData?.nonce,
                },
                success: function (response) {
                    if (response.success) {
                        // Complete the removal animation
                        $cartItem.css({
                            'max-height': $cartItem.outerHeight() + 'px',
                            'overflow': 'hidden',
                        });

                        requestAnimationFrame(() => {
                            $cartItem.css({
                                'opacity': '0',
                                'transform': 'translateX(-20px)',
                                'max-height': '0',
                                'padding-top': '0',
                                'padding-bottom': '0',
                                'margin-top': '0',
                                'margin-bottom': '0',
                            });
                        });

                        // Remove from DOM after animation
                        setTimeout(() => {
                            $cartItem.remove();
                        }, 300);

                        // Get Alpine component and use its methods
                        const checkoutEl = document.querySelector('[x-data*="checkoutNew"]');
                        const alpine = checkoutEl?._x_dataStack?.[0] || checkoutEl?.__x?.$data;
                        if (alpine) {

                            // Add to removed items and show undo toast (skip deal items)
                            if (!$cartItem.data('deal-id')) {
                                alpine.addRemovedItem(itemData);
                                alpine.showUndoToast(itemData);
                            }

                            // Update cart totals
                            alpine.updateCartTotals(response.data.cart_total, response.data.item_count);
                        } else {
                            updateCartTotalsUI(response.data);
                        }

                        // Shipping sync must not depend on the optional Alpine UI.
                        if (Number(response.data.item_count) === 0) {
                            if (alpine) alpine.cartEmpty = true;
                            hideCheckoutErrorsAndContent();
                            completeCartTotalsLoading(loadingToken);
                        } else {
                            // A removal can cross a table-rate price tier. Suspend
                            // Kustom first, then let CSA refetch/reselect the same
                            // service + pickup point at its current price. Without
                            // this event CSA retained its old free rate (#640513).
                            window.dispatchEvent(new CustomEvent('versa-kustom:cart-changed'));
                            $(document.body).trigger('csa:cart_changed');
                        }

                        window.dispatchEvent(new CustomEvent('versa-deals:cart-changed'));

                        // Auto-collapse cart in compact mode
                        autoCollapseCart();

                        log('Item removed successfully, undo available');
                    } else {
                        log('Error removing item:', response.data?.message);
                        completeCartTotalsLoading(loadingToken);
                        // Revert animation
                        $cartItem.css({
                            'opacity': '1',
                            'transform': 'none',
                        });
                        $btn.prop('disabled', false);
                    }
                },
                error: function () {
                    log('AJAX error removing item');
                    completeCartTotalsLoading(loadingToken);
                    // Revert animation
                    $cartItem.css({
                        'opacity': '1',
                        'transform': 'none',
                    });
                    $btn.prop('disabled', false);
                },
            });
        });
    }

    /**
     * Hide all checkout-related errors and content when cart becomes empty
     * Uses MutationObserver to catch dynamically injected error messages
     */
    function hideCheckoutErrorsAndContent() {
        log('Hiding checkout errors and content for empty cart');

        // Selectors for elements to hide
        const hideSelectors = [
            '.woocommerce-notices-wrapper',
            '.woocommerce-error',
            '.woocommerce-message',
            '.woocommerce-info',
            '.kco-message',
            '#kco-wrapper',
            '#kco-snippet-container',
            '#kco-loading-placeholder',
            '.checkout-new-sections',
            '.shipping-section',
            '.payment-section',
            '[data-section="shipping"]',
            '[data-section="payment"]',
        ];

        // Hide all matching elements
        hideSelectors.forEach(selector => {
            document.querySelectorAll(selector).forEach(el => {
                el.style.display = 'none';
            });
        });

        // Clear KCO to prevent any further activity
        const kcoContainer = document.getElementById('kco-snippet-container');
        if (kcoContainer) {
            kcoContainer.innerHTML = '';
            kcoContainer.dataset.loaded = 'false';
        }

        // Suspend KCO if it exists
        if (window._klarnaCheckout) {
            try {
                window._klarnaCheckout.suspend();
                log('Suspended KCO');
            } catch (e) {
                log('Could not suspend KCO:', e);
            }
        }

        // Note: MutationObserver removed - was causing issues by hiding empty cart state

        // Also hide the main checkout form content (except for our empty cart state)
        const checkoutForm = document.querySelector('form.checkout');
        if (checkoutForm) {
            // Hide form children except empty cart wrapper
            Array.from(checkoutForm.children).forEach(child => {
                if (!child.classList.contains('empty-cart-wrapper') && !child.querySelector('.empty-cart-wrapper')) {
                    child.style.display = 'none';
                }
            });
        }

        // Make sure empty cart wrapper and state are visible
        const emptyCartWrapper = document.querySelector('.empty-cart-wrapper');
        if (emptyCartWrapper) {
            emptyCartWrapper.style.display = 'block';
        }

        // Force the empty cart state to be visible (bypass Alpine x-show/x-cloak)
        const emptyCartState = document.querySelector('.empty-cart-state');
        if (emptyCartState) {
            emptyCartState.removeAttribute('x-cloak');
            emptyCartState.style.cssText = 'display: block !important;';
            log('Forced empty cart state visible');
        }

        // Keep checking and re-showing for a few seconds (counter other scripts that might hide it)
        let keepVisibleCount = 0;
        const keepVisibleInterval = setInterval(() => {
            keepVisibleCount++;

            // Re-apply visibility to empty cart elements
            const wrapper = document.querySelector('.empty-cart-wrapper');
            const state = document.querySelector('.empty-cart-state');

            if (wrapper) wrapper.style.cssText = 'display: block !important;';
            if (state) {
                state.removeAttribute('x-cloak');
                state.style.cssText = 'display: block !important;';
            }

            // Hide any error messages that appear
            document.querySelectorAll('.woocommerce-error, .woocommerce-info, .kco-message').forEach(el => {
                el.style.cssText = 'display: none !important;';
            });

            // Stop after 3 seconds (30 iterations at 100ms)
            if (keepVisibleCount >= 30) {
                clearInterval(keepVisibleInterval);
                log('Stopped keep-visible interval');
            }
        }, 100);
    }

    /**
     * Update cart totals in the DOM
     */
    function updateCartTotals(data) {
        log('Updating cart totals:', data);

        // Update cart total displays
        if (data.cart_total) {
            $('.cart-total-value, .order-total .woocommerce-Price-amount').html(data.cart_total);
        }

        // Update subtotal
        if (data.cart_subtotal) {
            $('.cart-subtotal-value').html(data.cart_subtotal);
        }

        // Update item count badge
        if (data.item_count !== undefined) {
            $('.cart-section .bg-versa-100').text(data.item_count + ' ' + (data.item_count === 1 ? 'produkt' : 'produkter'));
        }

        // Update shipping total if displayed
        if (data.shipping_total) {
            $('.cart-shipping-value').html(data.shipping_total);
        }

        // Update cart summary (collapsed view)
        if (data.cart_summary) {
            $('.cart-summary-items').text(data.cart_summary);
        }
        if (data.cart_total) {
            $('.cart-summary-total').html(data.cart_total + ' <span class="font-normal text-gray-500">' + (window.checkoutNewData?.i18n?.inclVat || '(incl. VAT)') + '</span>');
        }

        syncCartTotalsLoadingState();

        // Update per-item prices (e.g. after coupon apply/remove)
        if (data.item_prices) {
            $.each(data.item_prices, function (key, info) {
                var $item = $('.cart-item[data-cart-item-key="' + key + '"]');
                if (!$item.length) return;
                var $priceCell = $item.find('.text-right').last();
                if (info.has_discount) {
                    $priceCell.html(
                        '<span class="text-xs text-gray-400 line-through block">' + info.original_price_html + '</span>' +
                        '<span class="text-sm font-semibold text-red-600">' + info.price_html + '</span>' +
                        '<span class="text-xs text-gray-500">' + (window.checkoutNewData?.i18n?.perPiece || '/st') + '</span>'
                    );
                } else {
                    $priceCell.html(
                        '<span class="text-sm font-semibold text-gray-900">' + info.price_html + '</span>' +
                        '<span class="text-xs text-gray-500">' + (window.checkoutNewData?.i18n?.perPiece || '/st') + '</span>'
                    );
                }
            });
        }

        // Trigger cart_changed event so CSA refetches shipping options
        // (e.g., when cart total crosses free shipping threshold)
        $(document.body).trigger('csa:cart_changed');
    }

    /**
     * Handle coupon application
     */
    function initCouponHandler() {
        $(document).on('click', '.checkout-new-apply-coupon', function (e) {
            e.preventDefault();

            const $btn = $(this);
            const $input = $btn.siblings('input[name="coupon_code"]');
            const $message = $btn.closest('.coupon-input').find('.coupon-message');
            const couponCode = $input.val().trim();

            if (!couponCode) {
                return;
            }

            log('Applying coupon:', couponCode);

            // Show loading state
            const originalText = $btn.text();
            $btn.prop('disabled', true).text('...');
            $message.addClass('hidden');

            $.ajax({
                url: window.checkoutNewData?.ajaxUrl,
                method: 'POST',
                data: {
                    action: 'checkout_new_apply_coupon',
                    coupon_code: couponCode,
                    _wpnonce: window.checkoutNewData?.nonce,
                },
                success: function (response) {
                    log('Coupon response:', response);

                    if (response.success) {
                        $input.val('');
                        $message.removeClass('hidden text-red-600').addClass('text-green-600').text(response.data?.message || 'Coupon applied');

                        // Update cart totals
                        updateCartTotals(response.data);

                        // Inject coupon HTML into applied coupons container
                        if (response.data?.coupon_html) {
                            let $appliedCoupons = $('.applied-coupons');
                            if (!$appliedCoupons.length) {
                                // Create container if it doesn't exist
                                $appliedCoupons = $('<div class="applied-coupons mb-3 space-y-2"></div>');
                                $('.cart-coupon').prepend($appliedCoupons);
                            }
                            $appliedCoupons.append(response.data.coupon_html);
                        }

                        // Update discount row in cart totals
                        if (response.data?.has_discount && response.data?.discount_total) {
                            let $discountRow = $('.cart-discount-row');
                            if (!$discountRow.length) {
                                // Create discount row if it doesn't exist
                                const discountHtml = '<div class="flex justify-between text-sm cart-discount-row"><span class="text-gray-600">' + (window.checkoutNewData?.i18n?.discount || 'Discount') + '</span><span class="text-red-600 cart-discount-value">-' + response.data.discount_total + '</span></div>';
                                $('.cart-subtotal-value').closest('.flex').after(discountHtml);
                            } else {
                                $('.cart-discount-value').html('-' + response.data.discount_total);
                            }
                        }

                        // Reload KCO if it's currently loaded
                        window.dispatchEvent(new CustomEvent('versa-kustom:cart-changed'));

                        // Auto-collapse cart in compact mode
                        autoCollapseCart();

                        // Hide success message after 3 seconds
                        setTimeout(function () {
                            $message.addClass('hidden');
                        }, 3000);
                    } else {
                        $message.removeClass('hidden text-green-600').addClass('text-red-600').text(response.data?.message || 'Failed to apply coupon');
                    }
                },
                error: function () {
                    $message.removeClass('hidden text-green-600').addClass('text-red-600').text('Failed to apply coupon. Please try again.');
                },
                complete: function () {
                    $btn.prop('disabled', false).text(originalText);
                },
            });
        });

        // Handle coupon removal
        $(document).on('click', '.checkout-new-remove-coupon', function (e) {
            e.preventDefault();

            const $btn = $(this);
            const couponCode = $btn.data('coupon');

            if (!couponCode) {
                return;
            }

            log('Removing coupon:', couponCode);

            $btn.css('opacity', '0.5');

            $.ajax({
                url: window.checkoutNewData?.ajaxUrl,
                method: 'POST',
                data: {
                    action: 'checkout_new_remove_coupon',
                    coupon_code: couponCode,
                    _wpnonce: window.checkoutNewData?.nonce,
                },
                success: function (response) {
                    if (response.success) {
                        // Remove the coupon element
                        const $couponItem = $btn.closest('.applied-coupons > div');
                        $couponItem.fadeOut(200, function () {
                            $(this).remove();
                            // If no more coupons, remove the container
                            if ($('.applied-coupons').children().length === 0) {
                                $('.applied-coupons').remove();
                            }
                        });

                        // Update cart totals
                        updateCartTotals(response.data);

                        // Update or remove discount row
                        if (!response.data?.has_discount) {
                            $('.cart-discount-row').fadeOut(200, function () {
                                $(this).remove();
                            });
                        } else {
                            $('.cart-discount-value').html('-' + response.data.discount_total);
                        }

                        // Reload KCO if it's currently loaded
                        window.dispatchEvent(new CustomEvent('versa-kustom:cart-changed'));
                    } else {
                        $btn.css('opacity', '1');
                        alert(response.data?.message || 'Failed to remove coupon');
                    }
                },
                error: function () {
                    $btn.css('opacity', '1');
                },
            });
        });
    }

})(jQuery);
